/**
 * frontend/src/modules/performance/lib/api.ts
 * Helper pour appeler les endpoints Performance.
 *
 * Backend réel : GET /api/v1/performance/kpis/{slug} + query params
 *                GET /api/v1/performance/merchandisers[/:merch_code]
 *
 * Phase 4.I.4 · spec_performance.md v1.0 · DECISIONS #081
 */

import { apiClient } from '@/lib/apiClient';
import type { PerfApiResponse, PerformanceApiParams } from '../types/performance.types';

/** Sérialise les params en URLSearchParams (filtre undefined/null) */
function toSearchParams(params: PerformanceApiParams): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/**
 * Fetch un KPI équipe depuis GET /api/v1/performance/kpis/{slug}.
 */
export async function fetchPerfKpi<T>(
  slug: string,
  params?: PerformanceApiParams,
): Promise<PerfApiResponse<T>> {
  const qs = params ? toSearchParams(params) : '';
  const res = await apiClient.get<PerfApiResponse<T>>(`/performance/kpis/${slug}${qs}`);
  return res.data;
}

/**
 * Fetch la liste merchandisers depuis GET /api/v1/performance/merchandisers.
 * Réponse : { data: MerchandisersListData }
 */
export async function fetchMerchandisers<T>(
  params?: PerformanceApiParams,
): Promise<{ data: T }> {
  const qs = params ? toSearchParams(params) : '';
  const res = await apiClient.get<{ data: T }>(`/performance/merchandisers${qs}`);
  return res.data;
}

/**
 * Fetch le drill-down d'un merchandiser depuis GET /api/v1/performance/merchandisers/:merch_code.
 * Réponse : { data: MerchSummaryRow, meta: PerfMeta }
 */
export async function fetchMerchandiserDetail<T>(
  merchCode: string,
  params?: Omit<PerformanceApiParams, 'merch_code'>,
): Promise<PerfApiResponse<T>> {
  const qs = params ? toSearchParams(params) : '';
  const res = await apiClient.get<PerfApiResponse<T>>(
    `/performance/merchandisers/${encodeURIComponent(merchCode)}${qs}`,
  );
  return res.data;
}

/** P9 P5 F4 — Fetch collecte data ou AR signals depuis GET /performance/merch/:code/{slug}. */
export async function fetchMerchSubResource<T>(
  merchCode: string,
  slug: 'collecte' | 'ar-signals' | 'gps-clusters' | 'gps-begin-end',
  params?: Omit<PerformanceApiParams, 'merch_code'>,
): Promise<PerfApiResponse<T>> {
  const qs = params ? toSearchParams(params) : '';
  const res = await apiClient.get<PerfApiResponse<T>>(
    `/performance/merch/${encodeURIComponent(merchCode)}/${slug}${qs}`,
  );
  return res.data;
}
